import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Drawer, IconButton, SvgIcon, Tooltip, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { SlideProps } from '@material-ui/core/Slide';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExploreOutlinedIcon from '@material-ui/icons/ExploreOutlined';
import WidgetsOutlinedIcon from '@material-ui/icons/WidgetsOutlined';
import EventSystem from '../../EventSystem';
import { HEADER_HEIGHT } from '../../components/MainDrawer';
import { FOOTER_HEIGHT } from '../../components/AppFooter';
import { YourEnquiriesWidget } from './widgets/YourEnquiriesWidget';
import { YourTasksWidget } from './widgets/YourTasksWidget';
import { KpiSummaryWidget } from './widgets/KpiSummaryWidget';
import { EnquiryFollowUpsDueWidget } from './widgets/EnquiryFollowUpsDueWidget';
import { ContactFollowUpsDueWidget } from './widgets/ContactFollowUpsDueWidget';
import { TasksDueWidget } from './widgets/TasksDueWidget';
import { QuickViewWidgetSlot } from './widgets/QuickViewWidgetSlot';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { EnquiryFormDrawer } from '../../enquirySummary/drawers/EnquiryFormDrawer';
import { TasksSummaryFormDialog } from '../../tasksSummary/dialog/TasksSummaryFormDialog';
import { PhoneFormDialog } from '../../tasksSummary/dialog/PhoneFormDialog';
import { TaskSummary, fetchAppUserData } from '../../tasksSummary/TasksSummaryRepository';
import { fetchHomeCountry } from '../../enquirySummary/EnquirySummaryRepository';
import { retrievePOBoxTypeData } from '../../lookupConfiguration/selectors/POBoxTypeSelector';
import { showNotification } from '../../App';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';

// This web app's own brand blue (theme.palette.primary.main / mainTheme.TemplateColor.Primary,
// src/index.tsx) - same accent KpiSummaryWidget.tsx/YourEnquiriesWidget.tsx/
// YourTasksWidget.tsx already use, so the header reads as one family with the cards below it
// rather than plain app chrome sitting on top of a differently-tinted set of cards.
const QUICK_VIEW_ACCENT = '#5173FF';

// Single source of truth for the panel's width - AppContent.tsx imports this too, to push
// the page content over by exactly this much when pinned (rather than a second hardcoded
// number that could silently drift out of sync with this one).
export const QUICK_VIEW_WIDTH = 428;

// Every widget listens for this to refetch its own data - see each widget's own "onRefresh"
// useEffect. A plain EventSystem broadcast (rather than passing a refetch callback down into
// each widget as a prop) keeps the shell decoupled from each widget's own data-fetching
// mechanism (some use Apollo's useQuery, some a callback-based repository call) - see this
// folder's README for why each widget owns its own data.
export const QUICK_VIEW_REFRESH_EVENT = 'quickView:refresh';
// Asks QuickViewTourController (rendered by AppContent) to play the Quick View tour - the ? in the header.
export const QUICK_VIEW_TOUR_EVENT = 'quickView:tour';

// Stable identifiers for each widget, independent of feature access/display order - persisted
// to localStorage (see loadWidgetOrder below) so a person's own drag-to-reorder survives a
// refresh/re-login, same "remembers" treatment as appMenuDock (AppContent.tsx) and
// quickViewPinned. The array below is also this panel's own default/original order.
type QuickViewWidgetKey = 'yourEnquiries' | 'yourTasks' | 'kpiSummary' | 'enquiryFollowUpsDue' | 'contactFollowUpsDue' | 'tasksDue';
const DEFAULT_WIDGET_ORDER: QuickViewWidgetKey[] = [
    'yourEnquiries', 'yourTasks', 'kpiSummary', 'enquiryFollowUpsDue', 'contactFollowUpsDue', 'tasksDue',
];
const QUICK_VIEW_WIDGET_ORDER_STORAGE_KEY = 'quickViewWidgetOrder';

// Merges the saved order back in against DEFAULT_WIDGET_ORDER rather than trusting it outright
// - drops anything no longer recognised (a stale key from a since-removed widget) and appends
// any key the saved order is missing (a widget added after that person's own order was last
// saved) at the end, so a future new widget still shows up instead of silently disappearing.
function loadWidgetOrder(): QuickViewWidgetKey[] {
    try {
        const raw = localStorage.getItem(QUICK_VIEW_WIDGET_ORDER_STORAGE_KEY);
        if (!raw) {
            return DEFAULT_WIDGET_ORDER;
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) {
            return DEFAULT_WIDGET_ORDER;
        }
        const knownKeys: QuickViewWidgetKey[] = DEFAULT_WIDGET_ORDER;
        const savedOrder = parsed.filter((key): key is QuickViewWidgetKey => knownKeys.includes(key));
        const missingKeys = knownKeys.filter((key) => !savedOrder.includes(key));
        return [...savedOrder, ...missingKeys];
    } catch {
        return DEFAULT_WIDGET_ORDER;
    }
}

// Same UserFeatureEnum gates the render site used to check inline - pulled out as its own
// function so both the render itself and the drag/reorder logic (which needs to know which
// slots are actually on screen to measure/reorder) share one source of truth.
function isWidgetKeyVisible(key: QuickViewWidgetKey): boolean {
    switch (key) {
        case 'yourEnquiries':
        case 'enquiryFollowUpsDue':
            return UserFeature.HasAccess(UserFeatureEnum.hasLeadsSummary);
        case 'yourTasks':
        case 'tasksDue':
            return UserFeature.HasAccess(UserFeatureEnum.hasTaskSummary);
        case 'contactFollowUpsDue':
            return UserFeature.HasAccess(UserFeatureEnum.hasNameSummary);
        case 'kpiSummary':
            return UserFeature.HasAccess(UserFeatureEnum.hasShowFeeEarnerDashboard);
        default:
            return false;
    }
}

// Moves draggedKey to sit at targetVisibleIndex among only the VISIBLE keys, leaving any
// currently-hidden key (someone without access to that widget) exactly where it already was in
// the full order - so a person who later gains access to a widget they don't see today doesn't
// have their own past reordering silently disrupted by it reappearing somewhere unexpected.
function reorderWidgets(prevOrder: QuickViewWidgetKey[], draggedKey: QuickViewWidgetKey, targetVisibleIndex: number): QuickViewWidgetKey[] {
    const visibleKeys = prevOrder.filter(isWidgetKeyVisible);
    const currentVisibleIndex = visibleKeys.indexOf(draggedKey);
    if (currentVisibleIndex === -1) {
        return prevOrder;
    }
    const reorderedVisible = [...visibleKeys];
    reorderedVisible.splice(currentVisibleIndex, 1);
    // targetVisibleIndex is react-beautiful-dnd's own result.destination.index - already the
    // FINAL index draggedKey should land at (its own convention, not "insert before this slot
    // in the pre-removal array"), so it's used as-is here with no further adjustment. An
    // earlier version of this function subtracted 1 when dragging downward (a holdover from a
    // since-replaced hand-rolled drag implementation with different index semantics) - that
    // silently landed every downward drag one slot short of where it was dropped, most
    // visible as "can't drag to the very bottom" (explicit bug report).
    reorderedVisible.splice(targetVisibleIndex, 0, draggedKey);

    let visibleCursor = 0;
    return prevOrder.map((key) => (isWidgetKeyVisible(key) ? reorderedVisible[visibleCursor++] : key));
}

// Module-level, not React state - the definitive "is Quick View showing as a docked panel
// right now" flag, kept in sync by this component's own effect further down (see
// QUICK_VIEW_DOCKED_CHANGED_EVENT). Deliberately NOT the same thing as the persisted pin
// *preference* (localStorage's own 'quickViewPinned', read once by AppContent.tsx's
// constructor to decide whether to auto-reopen pinned on login) - AppContent.tsx's own
// onToggleQuickView now clears that preference too when closing a pinned panel via the ✕
// button, but there's still a gap a live flag needs to cover: this component (and its
// props.isPinned) doesn't exist at all before Quick View first mounts, or in the instant
// between a click and this component's own effect committing, so a hook reading the
// preference directly could still report a stale "docked" during that window. This tracks
// the real, current state instead.
let isQuickViewCurrentlyDocked = false;
export const QUICK_VIEW_DOCKED_CHANGED_EVENT = 'quickView:dockedChanged';

// For any page that opens a record drawer of its own (EnquiryFormDrawer, etc.) and wants to
// match how it opens from inside Quick View itself when Quick View happens to be pinned -
// i.e. starting from Quick View's own left edge (anchorOffset={QUICK_VIEW_WIDTH}) instead of
// covering it, but ONLY while Quick View is actually open and pinned right now (explicit bug
// report: "when the quick view is not docked, or closed, the enquiry forms should work as
// how it did before"). See EnquirySummaryPage.tsx for a call site.
export function useIsQuickViewPinned(): boolean {
    const [isDocked, setIsDocked] = useState(isQuickViewCurrentlyDocked);
    useEffect(() => {
        // Re-syncs once more here in case isQuickViewCurrentlyDocked already changed
        // between this hook's own initial useState computation above and this effect
        // actually running (e.g. QuickViewDrawer's own effect committing first).
        setIsDocked(isQuickViewCurrentlyDocked);
        const onDockedChanged = () => setIsDocked(isQuickViewCurrentlyDocked);
        EventSystem.on(QUICK_VIEW_DOCKED_CHANGED_EVENT, onDockedChanged);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_DOCKED_CHANGED_EVENT, onDockedChanged); };
    // tslint:disable-next-line: align
    }, []);
    return isDocked;
}

// Same rotated-pin glyph MainDrawer.tsx already uses for the nav sidebar's own dock toggle
// (outlined = click to pin, filled = pinned/click to unpin) - kept local rather than
// exported from there, matching this folder's own established practice of not reaching into
// shared/unrelated files for a small visual quote (see YourEnquiriesWidget.tsx's own comment
// on MOBILE_ACCENT_TEAL for the same reasoning).
const PinOutlinedIcon = (svgProps: { style?: React.CSSProperties }) => (
    <SvgIcon style={svgProps.style} viewBox="0 0 24 24">
        <g transform="rotate(45 12 12)">
            <path d="M17,2H7C6.45,2,6,2.45,6,3s0.45,1,1,1h1v5c0,1.66-1.34,3-3,3H4.5C4.22,12,4,12.22,4,12.5
                     S4.22,13,4.5,13h6.53V20l1,1l1-1v-7H19.5c0.28,0,0.5-0.22,0.5-0.5S19.78,12,19.5,12H19
                     c-1.66,0-3-1.34-3-3V4h1c0.55,0,1-0.45,1-1S17.55,2,17,2z
                     M14,4v5c0,1.12,0.37,2.16,1,3H9c0.65-0.86,1-1.9,1-3V4H14z" />
        </g>
    </SvgIcon>
);
const PinFilledIcon = (svgProps: { style?: React.CSSProperties }) => (
    <SvgIcon style={svgProps.style} viewBox="0 0 24 24">
        <g transform="rotate(45 12 12)">
            <path d="M17,2H7C6.45,2,6,2.45,6,3s0.45,1,1,1h1v5c0,1.66-1.34,3-3,3H4.5C4.22,12,4,12.22,4,12.5
                     S4.22,13,4.5,13H11v7l1,1l1-1v-7h6.5c0.28,0,0.5-0.22,0.5-0.5S20.28,12,20,12h-0.5
                     C17.34,12,16,10.66,16,9V4h1c0.55,0,1-0.45,1-1S17.55,2,17,2z" />
        </g>
    </SvgIcon>
);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            // Narrowed to a single widget column, per explicit request - this is a
            // vertically-scrolling stack of cards, not a wide multi-column workspace.
            width: QUICK_VIEW_WIDTH,
            maxWidth: '100vw',
            backgroundColor: '#F7F8FA',
            // Sits strictly between the fixed header and the footer, rather than the
            // default top:0/full-height - explicit request, it shouldn't overlap either
            // one's own line. Same top MainDrawer.tsx's own OverlayPanel/DockedSidebar use
            // for the nav sidebar; height additionally clears FOOTER_HEIGHT too, which the
            // nav sidebar doesn't need to (it sits behind/above the footer already, this
            // panel was extending past it). AppContent.tsx's own header z-index bump is
            // still needed separately, for UserCardV2.tsx's non-portaled dropdown menu,
            // which can still extend down past HEADER_HEIGHT into this panel's territory.
            top: `${HEADER_HEIGHT} !important`,
            height: `calc(100vh - ${HEADER_HEIGHT} - ${FOOTER_HEIGHT}) !important`,
            // No explicit borderTop here (removed after several rounds of trying to match its
            // colour to the header's own edge - BackgroundGrey3, then rgba(81,115,255,0.17),
            // then theme.palette.divider) - explicit bug report/screenshot that this line
            // itself stands out against the header regardless of colour, since a hard 1px
            // line and the header's own soft box-shadow edge never truly read as "the same
            // line". Leaving it unset reads as correct instead.
            // Explicit re-enable to pair with the Drawer's own ModalProps.style pointer-
            // events:none while pinned (see that prop's own comment) - harmless when that
            // isn't in play (pointer-events already defaults to auto for everything else),
            // required when it is, since pointer-events:none on an ancestor is inherited by
            // descendants that don't explicitly opt back in.
            pointerEvents: 'auto',
        },
        // Applied only while pinned (in addition to `paper` above) - a docked, always-there
        // panel reads as structural furniture, not a floating overlay, so it gets a plain
        // border like the nav sidebar's own DockedSidebar instead of a drop shadow (which
        // read as "temporary" and was part of what made pinned mode feel unpolished -
        // elevation is set to 0 alongside this wherever it's used).
        paperDocked: {
            borderLeft: `1px solid ${theme.palette.divider}`,
        },
        headerActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
        },
        // Spins the refresh icon while a refresh is in flight - a fixed one-shot spin (not
        // "spin until some promise resolves") since the shell has no way to know when every
        // widget's own independent refetch has actually finished (see QUICK_VIEW_REFRESH_EVENT's
        // own comment) - this is feedback that the click registered, not a progress indicator.
        refreshIconSpinning: {
            animation: '$spin 700ms linear',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
        },
        header: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            // Same padding/gap as PageContentLayout.tsx's own pageHeader (EnquirySummaryPage/
            // TasksSummaryPage/etc.'s header) - explicit request, this panel's own header
            // should match that height exactly rather than its own earlier, taller values.
            gap: theme.spacing(1.25),
            padding: theme.spacing(1.25, 2),
            backgroundColor: 'var(--dc-surface)',
            // A soft accent bar rather than a flat rule - same tint the KPI/hero cards
            // above use (QUICK_VIEW_ACCENT), so the header reads as part of the same family
            // instead of a plain, unrelated app-chrome bar. position:absolute so it can never
            // add to this header's own height (unlike PageContentLayout's plain borderBottom,
            // which is 1px either way and wouldn't have mattered, but this bar's job is
            // purely decorative, not load-bearing for the height match above).
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 2,
                background: `linear-gradient(90deg, ${QUICK_VIEW_ACCENT} 0%, ${QUICK_VIEW_ACCENT}00 75%)`,
            },
        },
        headerIconChip: {
            // Same 36px/8px as PageContentLayout.tsx's own pageHeaderIcon - only the tint
            // stays this panel's own accent rather than that one's fixed light-blue, so it
            // still reads as one family with the KPI/hero cards below it.
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${QUICK_VIEW_ACCENT}1F`,
            color: QUICK_VIEW_ACCENT,
        },
        headerText: {
            flex: 1,
            minWidth: 0,
        },
        title: {
            // Same size/weight as PageContentLayout.tsx's own pageHeaderTitle.
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
        },
        subtitle: {
            // Same size as PageContentLayout.tsx's own pageHeaderSubtitle.
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 1,
        },
        body: {
            flex: 1,
            overflowY: 'auto',
            padding: theme.spacing(3),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2.5),
            // Flex items default to flex-shrink:1, so once the stacked cards' combined height
            // exceeds the drawer's viewport, the column would otherwise squeeze each card
            // below its natural height instead of overflowing into the scroll above - and
            // since each card clips its own content with overflow:hidden (for its watermark),
            // that shrinkage was cutting cards off rather than just scrolling to them.
            '& > *': {
                flexShrink: 0,
            },
        },
    })
);

export interface QuickViewDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Docked-open (pushes page content over, no backdrop, survives navigation) vs a
     * temporary overlay (current default: backdrop, closes on navigate/outside click). */
    isPinned: boolean;
    onTogglePin: () => void;
    /** The Quick View tour is running: its card lives outside this drawer, so the drawer must not pull focus back or close on Escape. */
    isTourRunning?: boolean;
}

interface OpenEnquiry {
    guid: string;
    isKeyOpportunity: boolean;
}

interface RecordDrawerState {
    // Supporting context EnquiryFormDrawer requires even to view/edit an existing enquiry
    // (address-field dropdowns) - fetched once here rather than per-widget, the same
    // one-time-fetch TasksSummaryPage.tsx itself does for the same drawer.
    homeCountry: string;
    pOBoxType: string | null;
    userId: string;
    userName: string;
    openEnquiry: OpenEnquiry | null;
    openTaskGuid: string | null;
    openPhoneTaskGuid: string | null;
}

// The right-side "at a glance" panel - see this folder's README for the full brief.
// Mounted in AppContent.tsx alongside TimesheetTimerDrawer/UploadDocumentDialog (outside
// the <Router> route switch), so opening it never navigates and it survives whatever page
// is underneath. AppContent also closes it automatically via browserHistory.listen() the
// moment the person actually navigates away (e.g. clicking a Quick Actions tile below, or
// any other link/nav item while the panel is open) - not handled in here, so that
// close-on-navigate behaviour lives in exactly one place regardless of what triggered it.
// Pinned is the explicit exception to that: AppContent.tsx's own listener skips the
// auto-close while isPinned is true, matching MainDrawer.tsx's own dockMode - a pinned
// panel is meant to stay visible while navigating around and performing other work, not
// just while sitting still on one page. isPinned drives AppContent.tsx's own marginRight
// on <main> (pushing its content over rather than overlaying it) and this component's own
// ModalProps below (no backdrop, no focus trap, no scroll lock, no Escape-to-close) -
// deliberately NOT a Drawer variant switch, see that prop's own comment for why.
export const QuickViewDrawer: React.FC<QuickViewDrawerProps> = (props) => {
    const classes = useStyles();

    // Keeps the module-level isQuickViewCurrentlyDocked (see useIsQuickViewPinned's own
    // comment) in sync with reality: this component only ever mounts while Quick View is
    // actually open (AppContent.tsx's own conditional render), so "docked right now" is
    // simply props.isPinned while mounted, and always false once unmounted (the cleanup
    // below) - regardless of whatever isPinned was at that moment, since closed is closed.
    useEffect(() => {
        isQuickViewCurrentlyDocked = props.isPinned;
        EventSystem.emit(QUICK_VIEW_DOCKED_CHANGED_EVENT);
        return () => {
            isQuickViewCurrentlyDocked = false;
            EventSystem.emit(QUICK_VIEW_DOCKED_CHANGED_EVENT);
        };
    // tslint:disable-next-line: align
    }, [props.isPinned]);

    // Explicit bug report: pinning while already open left the page unscrollable (fine again
    // only after a full refresh). Root cause - confirmed against MUI v4's own Modal.js source -
    // disableScrollLock (ModalProps below) is only ever read inside the effect gated on `open`
    // (Modal.js's own handleOpen, called from a useEffect keyed on [open, ...] - disableScrollLock
    // itself isn't a dependency). Since `open` is deliberately kept constant across a pin toggle
    // (the comment above this component explains why - avoids remounting all six widgets), that
    // effect never re-runs, so ModalManager never re-applies the lock with the new
    // disableScrollLock value: the overflow:hidden it set on <body> while unpinned (a real,
    // backdrop-having overlay, where the lock is correct) simply never gets released once pinned,
    // even though ModalProps.disableScrollLock has already flipped to true by then. Clearing the
    // exact two inline styles ModalManager's own handleContainer sets (body overflow/padding-right
    // - confirmed from its source, there's no .mui-fixed node elsewhere in this app needing the
    // same treatment) is a narrow, targeted mirror of what that manager would have done itself on
    // a fresh open with disableScrollLock already true - not a general scroll-lock override.
    useEffect(() => {
        if (props.open && props.isPinned && document.body.style.overflow === 'hidden') {
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        }
    // tslint:disable-next-line: align
    }, [props.isPinned, props.open]);

    const [recordState, setRecordState] = useState<RecordDrawerState>({
        homeCountry: '',
        pOBoxType: null,
        userId: '',
        userName: '',
        openEnquiry: null,
        openTaskGuid: null,
        openPhoneTaskGuid: null,
    });

    // One-time fetch of the context EnquiryFormDrawer needs, same as
    // TasksSummaryPage.tsx's own fetchHomeCountryOnEffect/fetchPoBoxTypeData/
    // fetchAppUserOnEffect - done once here rather than per-widget, since only opening that
    // one drawer needs any of it (TasksSummaryFormDialog/PhoneFormDialog need none of this
    // to view/edit an existing record - their own default-value props are create-flow only).
    useEffect(() => {
        fetchHomeCountry(
            false,
            (data) => {
                if (data?.options?.homeCountry !== undefined) {
                    setRecordState((prev) => ({
                        ...prev,
                        homeCountry: data.options.homeCountry.stringValue === null ? 'au' : data.options.homeCountry.stringValue,
                    }));
                }
            },
            (reason) => showNotification('Failed to Fetch Home Country Settings', reason, 'error')
        );

        retrievePOBoxTypeData(
            false,
            (data) => {
                if (data.lookup.poBoxType.length > 0) {
                    setRecordState((prev) => ({ ...prev, pOBoxType: data.lookup.poBoxType[0].description }));
                }
            },
            (reason) => showNotification(null, reason, 'error')
        );

        fetchAppUserData(
            false,
            (data) => {
                if (data?.appUser) {
                    setRecordState((prev) => ({ ...prev, userId: data.appUser.userId, userName: data.appUser.name }));
                }
            },
            (reason) => showNotification('Failed to Fetch App User Settings', reason, 'error')
        );
    // tslint:disable-next-line: align
    }, []);

    const onOpenEnquiry = (guid: string, isKeyOpportunity: boolean) => {
        setRecordState((prev) => ({ ...prev, openEnquiry: { guid, isKeyOpportunity } }));
    };
    const onCloseEnquiry = () => {
        setRecordState((prev) => ({ ...prev, openEnquiry: null }));
    };

    // Same routing TasksSummaryPage.tsx's own onEdit() uses: a task linked to an enquiry
    // (entityType 1, not a phone task) opens the enquiry drawer instead of a task dialog; a
    // PhoneTask opens PhoneFormDialog; everything else opens the plain TasksSummaryFormDialog.
    const onOpenTask = (task: TaskSummary) => {
        if (task.entityType === 1 && task.taskType !== 'PhoneTask') {
            if (task.entityGuidID) {
                onOpenEnquiry(task.entityGuidID, task.isKeyOpportunity);
            }
            return;
        }
        if (task.taskType === 'PhoneTask') {
            setRecordState((prev) => ({ ...prev, openPhoneTaskGuid: task.taskGuidID }));
            return;
        }
        setRecordState((prev) => ({ ...prev, openTaskGuid: task.taskGuidID }));
    };
    const onCloseTask = () => setRecordState((prev) => ({ ...prev, openTaskGuid: null }));
    const onClosePhoneTask = () => setRecordState((prev) => ({ ...prev, openPhoneTaskGuid: null }));

    // Explicit request: "drag and move these widgets up and down... save the order... so it is
    // remembered the next time the user logs back in" - same "remembers" treatment as
    // appMenuDock/quickViewPinned. widgetOrder always holds all six keys (see
    // reorderWidgets/loadWidgetOrder's own comments for why hidden keys still travel along);
    // visibleWidgetKeys below is what's actually rendered/draggable.
    const [widgetOrder, setWidgetOrder] = useState<QuickViewWidgetKey[]>(() => loadWidgetOrder());
    useEffect(() => {
        localStorage.setItem(QUICK_VIEW_WIDGET_ORDER_STORAGE_KEY, JSON.stringify(widgetOrder));
    // tslint:disable-next-line: align
    }, [widgetOrder]);

    const visibleWidgetKeys = widgetOrder.filter(isWidgetKeyVisible);

    // Explicit follow-up request ("should we use react-beautiful-dnd... to make the drag drop
    // smooth?") - replaces an earlier hand-rolled pointer-events implementation (custom
    // pointermove tracking + a manual FLIP animation) that felt laggy and had corner-radius
    // glitches. react-beautiful-dnd is already a dependency, used the same way for other
    // reorderable lists in this app (see featureConfigurationLayout/FeatureLayoutConfigurationDroppable.tsx),
    // and handles the smooth drag physics/sibling-shifting/accessibility itself - its own
    // native behaviour is siblings shifting live as the dragged card passes over them (not a
    // static list + a drop-line indicator), which is a deliberate trade against last request's
    // literal ask, in favour of the library's own well-tested interaction model.
    const onDragEnd = (result: DropResult) => {
        if (!result.destination || result.source.index === result.destination.index) {
            return;
        }
        const draggedKey = visibleWidgetKeys[result.source.index];
        const destinationIndex = result.destination.index;
        setWidgetOrder((prevOrder) => reorderWidgets(prevOrder, draggedKey, destinationIndex));
    };

    // onOpenEnquiry/onOpenTask closures live on this component, not the module-level
    // isWidgetKeyVisible/reorderWidgets above, so this stays a component-level function.
    const renderWidgetContent = (key: QuickViewWidgetKey): React.ReactNode => {
        switch (key) {
            case 'yourEnquiries': return <YourEnquiriesWidget />;
            case 'yourTasks': return <YourTasksWidget />;
            case 'kpiSummary': return <KpiSummaryWidget />;
            case 'enquiryFollowUpsDue': return <EnquiryFollowUpsDueWidget onOpenEnquiry={onOpenEnquiry} />;
            case 'contactFollowUpsDue': return <ContactFollowUpsDueWidget onOpenTask={onOpenTask} />;
            case 'tasksDue': return <TasksDueWidget onOpenTask={onOpenTask} />;
            default: return null;
        }
    };

    // MUI's own Drawer default (225ms enter, sharp-ish easing) reads a little mechanical for
    // a panel this size - a touch slower on the way in with a decelerating "glide" curve
    // (the same family of curve as most native OS panels/sheets) feels smoother without
    // being sluggish; closing stays snappier so dismissing doesn't feel laggy. Only actually
    // runs on a real open/close (`open` flipping) - toggling isPinned while already open
    // doesn't touch `open` at all, so it never replays this.
    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);
    const refreshSpinTimeoutRef = useRef<number | null>(null);
    const onRefresh = () => {
        EventSystem.emit(QUICK_VIEW_REFRESH_EVENT);
        setIsRefreshSpinning(true);
        if (refreshSpinTimeoutRef.current !== null) {
            window.clearTimeout(refreshSpinTimeoutRef.current);
        }
        refreshSpinTimeoutRef.current = window.setTimeout(() => setIsRefreshSpinning(false), 700);
    };
    useEffect(() => () => {
        if (refreshSpinTimeoutRef.current !== null) {
            window.clearTimeout(refreshSpinTimeoutRef.current);
        }
    }, []);

    const headerContent = (
        <div className={classes.header}>
            <span className={classes.headerIconChip}>
                <WidgetsOutlinedIcon fontSize="small" />
            </span>
            <div className={classes.headerText}>
                <Typography variant="subtitle1" className={classes.title}>Quick View</Typography>
                <Typography className={classes.subtitle}>Your day at a glance</Typography>
            </div>
            <div className={classes.headerActions}>
                <Tooltip title="How Quick View works">
                    <IconButton size="small" onClick={() => EventSystem.emit(QUICK_VIEW_TOUR_EVENT)} aria-label="How Quick View works">
                        <ExploreOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={onRefresh} aria-label="Refresh Quick View" data-tour="quick-view-refresh">
                        <RefreshIcon fontSize="small" className={isRefreshSpinning ? classes.refreshIconSpinning : undefined} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={props.isPinned ? 'Unpin panel' : 'Pin panel open'}>
                    <IconButton size="small" onClick={props.onTogglePin} aria-label={props.isPinned ? 'Unpin Quick View' : 'Pin Quick View'} data-tour="quick-view-pin">
                        {props.isPinned ? <PinFilledIcon style={{ fontSize: 18 }} /> : <PinOutlinedIcon style={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={props.onClose} aria-label="Close Quick View">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
        </div>
    );

    return (
        <Drawer
            anchor="right"
            open={props.open}
            onClose={props.onClose}
            // Deliberately always "temporary" - never variant="permanent" for pinned, even
            // though that's what MainDrawer.tsx's own dockMode uses for the nav sidebar.
            // Explicit bug report: switching variant while already open swaps the Drawer's
            // own underlying element type (a plain docked <div> vs a Modal), which forces
            // React to unmount and remount everything inside - all six widgets included, so
            // toggling pin was silently re-fetching every widget's data from scratch every
            // time. Keeping variant fixed means `open` never changes on a pin toggle, so
            // nothing inside this Drawer unmounts - only the Modal-level behaviours below
            // change, which is what actually distinguishes "docked" from "overlay" here
            // anyway (backdrop presence, focus trap, scroll lock, Escape-to-close).
            variant="temporary"
            // elevation:0 alongside paperDocked's own border when pinned - see that class's
            // comment for why a docked panel gets a border instead of a floating shadow.
            elevation={props.isPinned ? 0 : 16}
            classes={{ paper: clsx(classes.paper, props.isPinned && classes.paperDocked) }}
            PaperProps={{ 'data-tour': 'quick-view-panel' } as React.HTMLAttributes<HTMLElement>}
            ModalProps={{
                hideBackdrop: props.isPinned,
                disableEnforceFocus: props.isPinned || props.isTourRunning,
                disableAutoFocus: props.isPinned || props.isTourRunning,
                disableEscapeKeyDown: props.isPinned || props.isTourRunning,
                disableScrollLock: props.isPinned,
                // zIndex:1200 always (not just while pinned) - explicit bug report: the
                // user-menu dropdown and Self Help popover (both anchored in the app header)
                // were rendering behind Quick View even after the header's own z-index was
                // raised to 1250. That earlier fix compared against the wrong number:
                // Drawer's own Paper sits at theme.zIndex.drawer (1200), but the *Modal's own
                // wrapping root div* - confirmed directly against
                // @material-ui/core/Modal/Modal.js, the actual element being compared against
                // the header here, since they're unrelated siblings - defaults to
                // theme.zIndex.modal (1300), higher than the header's own 1250. Pinning this
                // Modal's root to 1200 explicitly (below the header, same tier as Drawer's own
                // Paper) fixes that without touching the header's own z-index again, and
                // without raising it past 1300, which would also cover genuine modal dialogs
                // (EnquiryFormDrawer, confirm dialogs, ...) that legitimately should stay above
                // the header. MUI v4's Modal isn't actually styled via a `classes` prop at all
                // despite its own .d.ts declaring one (confirmed same source: it computes
                // {position:'fixed', zIndex:theme.zIndex.modal, ...} via useTheme() and merges
                // it straight onto the root div's own `style` attribute, never reading
                // `classes`) - `style` IS merged in for real (spread in last, after that
                // computed object), so this is the only pathway that actually reaches the root
                // div; same reasoning covers pointerEvents:'none' while pinned (see that
                // fix's own earlier comment history).
                style: { zIndex: 1200, ...(props.isPinned ? { pointerEvents: 'none' } : {}) },
            }}
            BackdropProps={props.isPinned ? undefined : {
                style: {
                    backdropFilter: 'blur(1px)',
                    // Matches the panel's own top/height override (classes.paper) - without
                    // this the backdrop still covered the full viewport from y:0 to 100vh
                    // while the panel itself sat strictly between the header and footer,
                    // leaving a visible mismatched grey strip at both the top and bottom of
                    // the screen where the backdrop showed through but the panel didn't
                    // reach.
                    top: HEADER_HEIGHT,
                    height: `calc(100vh - ${HEADER_HEIGHT} - ${FOOTER_HEIGHT})`,
                },
            }}
            transitionDuration={prefersReducedMotion ? 0 : { enter: 320, exit: 220 }}
            // `easing` is a genuine runtime prop of the underlying Slide/Transition component -
            // just missing from @material-ui/core's own v4 SlideProps typings, hence the cast.
            SlideProps={prefersReducedMotion ? undefined : ({
                easing: {
                    enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    exit: 'cubic-bezier(0.7, 0, 0.84, 0)',
                },
            } as Partial<SlideProps>)}
        >
            {headerContent}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="quickViewWidgets">
                    {(droppableProvided) => (
                        <div className={classes.body} ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                            {/* Each widget is gated behind the same UserFeatureEnum flag the
                                equivalent page/menu entry uses (AppMenu.tsx for Enquiries/Tasks,
                                hasShowFeeEarnerDashboard for the KPI card - same flag
                                FeeEarnerDashboardPage's own menu item checks), matching the
                                mobile app's own HOME_WIDGET_SECTION_FEATURE_GATES mapping
                                (RedView Mobile src/config/featureGates.ts) - explicit bug
                                report: a Tasks-only user (no hasLeadsSummary/
                                hasShowFeeEarnerDashboard) was still seeing the enquiry and KPI
                                cards here, since none of these widgets checked access before
                                this fix; only the unused QuickActionsWidget.tsx did. Checked
                                here (rather than inside each widget) so a person without access
                                never mounts the widget at all - its useQuery never fires
                                either.

                                Draggable/reorderable via react-beautiful-dnd (see onDragEnd's
                                own comment for why) - persisted through widgetOrder's own
                                effect above (same "remembers" treatment as appMenuDock/
                                quickViewPinned).

                                Each widget fades/rises in on mount, staggered by index - same
                                AnimatedItem convention (and 30ms step) EnquirySummaryBoardDroppable.tsx
                                uses for its own Draggable cards. Wrapped around the widget content
                                rather than QuickViewWidgetSlot itself, so react-beautiful-dnd's own
                                ref/draggableProps (which must land on a real positioned element,
                                not an animation wrapper) stay exactly where they already were. Keys
                                are stable across a drag reorder, so React keeps the same instances
                                and this never replays mid-drag - only on the panel's own (re)mount. */}
                            {visibleWidgetKeys.map((key, index) => (
                                <Draggable key={key} draggableId={key} index={index}>
                                    {(draggableProvided, snapshot) => (
                                        <QuickViewWidgetSlot
                                            ref={draggableProvided.innerRef}
                                            draggableProps={draggableProvided.draggableProps}
                                            dragHandleProps={draggableProvided.dragHandleProps}
                                            isDragging={snapshot.isDragging}
                                            tourKey={key}
                                        >
                                            <AnimatedItem noFill delay={index * 30}>
                                                {renderWidgetContent(key)}
                                            </AnimatedItem>
                                        </QuickViewWidgetSlot>
                                    )}
                                </Draggable>
                            ))}
                            {droppableProvided.placeholder}
                            {/* Fast-follow, staged next - see this folder's README: Firm/Fee
                                Earner/Marketing Performance rows. */}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {recordState.openEnquiry && (
                <EnquiryFormDrawer
                    guid={recordState.openEnquiry.guid}
                    onDialogClose={onCloseEnquiry}
                    formAction="edit"
                    assignedBy={recordState.userId}
                    assignedByName={recordState.userName}
                    isEnquiryFormOpen={true}
                    isKeyOpportunity={recordState.openEnquiry.isKeyOpportunity}
                    homeCountry={recordState.homeCountry}
                    pOBoxType={recordState.pOBoxType}
                    // Explicit request: opening a record from here shouldn't cover Quick
                    // View entirely - starting this drawer from Quick View's own left edge
                    // instead of the viewport's keeps both visible side by side. Quick View
                    // itself never closes when a record is opened (it's a local component
                    // state change here, not a navigation - see this component's own top
                    // comment), so it's always still on screen at this width whenever this
                    // renders, whether pinned or the temporary overlay.
                    anchorOffset={QUICK_VIEW_WIDTH}
                />
            )}
            {recordState.openTaskGuid && (
                <TasksSummaryFormDialog
                    isOpen={true}
                    onClose={onCloseTask}
                    guid={recordState.openTaskGuid}
                    isCreate={false}
                />
            )}
            {recordState.openPhoneTaskGuid && (
                <PhoneFormDialog
                    isOpen={true}
                    onClose={onClosePhoneTask}
                    guid={recordState.openPhoneTaskGuid}
                    isCreate={false}
                />
            )}
        </Drawer>
    );
};
